import { type Editor, Extension } from '@tiptap/core';
import { type EditorState, Plugin, PluginKey, type Transaction } from '@tiptap/pm/state';
import Suggestion, { type SuggestionOptions } from '@tiptap/suggestion';

import {
  createWorkspaceAgentSkillMonikerInsertNodes,
  readWorkspaceAgentSkillMonikerAttrs,
  WORKSPACE_AGENT_SKILL_MONIKER_NODE_NAME,
} from '~/components/v2/components/workspace/workspace-agent-panel/tiptap/workspaceAgentSkillMoniker';
import { type Skill } from '~/hooks/queries/skills/usePrivateSkillsList';

export const WORKSPACE_AGENT_SKILL_SUGGESTION_PLUGIN_KEY = new PluginKey('workspaceAgentSkillSuggestion');

/** Transaction meta the `/` toolbar uses to reopen a dismissed slash suggestion on the existing `/`. */
export const WORKSPACE_AGENT_SKILL_REOPEN_META = 'workspaceAgentSkillReopen';

const WORKSPACE_AGENT_SKILL_AUTO_FORMAT_PLUGIN_KEY = new PluginKey('workspaceAgentSkillAutoFormat');

const WORKSPACE_AGENT_SKILL_CATALOG_UPGRADE_PLUGIN_KEY = new PluginKey('workspaceAgentSkillMonikerCatalogUpgrade');

/** Dispatched when the skills catalog loads or changes so pasted monikers can rehydrate display attrs. */
export const WORKSPACE_AGENT_SKILL_CATALOG_UPGRADE_REQUEST_META = 'workspaceAgentSkillMonikerCatalogUpgradeRequest';

export const requestWorkspaceAgentSkillMonikerCatalogUpgrade = (editor: Editor): void => {
  if (editor.isDestroyed) {
    return;
  }

  editor.view.dispatch(editor.state.tr.setMeta(WORKSPACE_AGENT_SKILL_CATALOG_UPGRADE_REQUEST_META, true));
};

/** Start-of-paragraph or whitespace before `/command ` — avoids rewriting URL path segments. */
export const WORKSPACE_AGENT_SLASH_COMMAND_RE = /(?:^|\s)(\/([a-z][a-z0-9-]*)\s)$/;

export type WorkspaceAgentSkillSuggestionExtensionOptions = {
  getSkillById: (skillId: string) => Skill | undefined;
  getSkills: () => readonly Skill[];
  hasSkillsCatalogNextPage: () => boolean;
  isSlashSkillsEnabled: () => boolean;
  requestSkillsCatalogNextPage: () => void;
  suggestion: Omit<SuggestionOptions<Skill, Skill>, 'editor'>;
};

type WorkspaceAgentSlashAutoFormatDeps = {
  getSkills: () => readonly Skill[];
  hasSkillsCatalogNextPage: () => boolean;
  requestSkillsCatalogNextPage: () => void;
};

const tryAppendWorkspaceAgentSlashCommandAutoFormat = (
  newState: EditorState,
  { getSkills, hasSkillsCatalogNextPage, requestSkillsCatalogNextPage }: WorkspaceAgentSlashAutoFormatDeps,
): Transaction | null => {
  const { selection } = newState;

  if (!selection.empty) {
    return null;
  }

  const { $from } = selection;
  const textBefore = newState.doc.textBetween($from.start(), $from.pos, undefined, '\ufffc');
  const match = textBefore.match(WORKSPACE_AGENT_SLASH_COMMAND_RE);

  if (!match) {
    return null;
  }

  const slashCommand = match[2];
  const skill = getSkills().find((entry) => entry.slashCommand === slashCommand);

  if (!skill) {
    if (hasSkillsCatalogNextPage()) {
      queueMicrotask(requestSkillsCatalogNextPage);
    }

    return null;
  }

  const insertNodes = createWorkspaceAgentSkillMonikerInsertNodes(newState.schema, {
    name: skill.name,
    skillId: skill.id,
    slashCommand: skill.slashCommand,
  });

  if (insertNodes.length === 0) {
    return null;
  }

  const slashSegment = match[1];
  const matchStart = $from.pos - slashSegment.length;
  const matchEnd = $from.pos;

  return newState.tr.replaceWith(matchStart, matchEnd, insertNodes);
};

export const filterWorkspaceAgentSkillsByQuery = (skills: readonly Skill[], query: string): Skill[] => {
  if (!query) {
    return [...skills];
  }

  const lower = query.toLowerCase();

  return skills.filter(
    (skill) =>
      skill.name.toLowerCase().includes(lower) ||
      skill.description.toLowerCase().includes(lower) ||
      skill.slashCommand.toLowerCase().includes(lower),
  );
};

/**
 * Loaded catalog pages only cover the first N skills by name, so a typed `/` query also needs
 * server-side `Skills.list({ query })` matches for skills on pages that have not been fetched yet.
 */
export const mergeWorkspaceAgentSkillSuggestionItems = (
  filteredLoadedSkills: readonly Skill[],
  searchedSkills: readonly Skill[],
): Skill[] => {
  const loadedIds = new Set(filteredLoadedSkills.map((skill) => skill.id));

  return [...filteredLoadedSkills, ...searchedSkills.filter((skill) => !loadedIds.has(skill.id))];
};

/**
 * Server search is debounced, so its rows can still be the previous query. Filter them by the live
 * slash text before merging, or Enter can insert a skill that no longer matches. An empty query is
 * the `/` browse of loaded pages only.
 */
export const buildWorkspaceAgentSkillSuggestionItems = ({
  loadedSkills,
  query,
  searchedSkills,
}: {
  loadedSkills: readonly Skill[];
  query: string;
  searchedSkills: readonly Skill[];
}): Skill[] =>
  mergeWorkspaceAgentSkillSuggestionItems(
    filterWorkspaceAgentSkillsByQuery(loadedSkills, query),
    query.length > 0 ? filterWorkspaceAgentSkillsByQuery(searchedSkills, query) : [],
  );

export const WorkspaceAgentSkillSuggestion = Extension.create<WorkspaceAgentSkillSuggestionExtensionOptions>({
  addOptions() {
    return {
      getSkillById: (): Skill | undefined => undefined,
      getSkills: (): Skill[] => [],
      hasSkillsCatalogNextPage: (): boolean => false,
      isSlashSkillsEnabled: (): boolean => false,
      requestSkillsCatalogNextPage: (): void => undefined,
      suggestion: {
        allowedPrefixes: [' '],
        allowSpaces: false,
        char: '/',
        pluginKey: WORKSPACE_AGENT_SKILL_SUGGESTION_PLUGIN_KEY,
        startOfLine: false,
      },
    };
  },

  addProseMirrorPlugins() {
    const { getSkillById, getSkills, hasSkillsCatalogNextPage, isSlashSkillsEnabled, requestSkillsCatalogNextPage } =
      this.options;

    const catalogUpgradePlugin = new Plugin({
      appendTransaction(transactions, _oldState, newState) {
        if (!isSlashSkillsEnabled()) {
          return null;
        }

        if (transactions.some((tr) => tr.getMeta(WORKSPACE_AGENT_SKILL_CATALOG_UPGRADE_PLUGIN_KEY))) {
          return null;
        }

        const catalogUpgradeRequested = transactions.some((tr) =>
          tr.getMeta(WORKSPACE_AGENT_SKILL_CATALOG_UPGRADE_REQUEST_META),
        );

        if (!catalogUpgradeRequested && !transactions.some((tr) => tr.docChanged)) {
          return null;
        }

        const upgrades: { attrs: { name: string; skillId: string; slashCommand: string }; pos: number }[] = [];
        let requestedCatalogPage = false;

        newState.doc.descendants((node, pos) => {
          if (node.type.name !== WORKSPACE_AGENT_SKILL_MONIKER_NODE_NAME) {
            return;
          }

          const parsed = readWorkspaceAgentSkillMonikerAttrs(node.attrs);

          if (!parsed || parsed.slashCommand) {
            return;
          }

          const skill = getSkillById(parsed.skillId);

          if (!skill) {
            if (!requestedCatalogPage && hasSkillsCatalogNextPage()) {
              requestedCatalogPage = true;
              queueMicrotask(requestSkillsCatalogNextPage);
            }

            return;
          }

          upgrades.push({
            attrs: {
              name: skill.name,
              skillId: skill.id,
              slashCommand: skill.slashCommand,
            },
            pos,
          });
        });

        if (upgrades.length === 0) {
          return null;
        }

        let tr = newState.tr;

        for (const { attrs, pos } of upgrades.reverse()) {
          tr = tr.setNodeMarkup(pos, undefined, attrs);
        }

        return tr.setMeta(WORKSPACE_AGENT_SKILL_CATALOG_UPGRADE_PLUGIN_KEY, true);
      },
      key: WORKSPACE_AGENT_SKILL_CATALOG_UPGRADE_PLUGIN_KEY,
    });

    const autoFormatPlugin = new Plugin({
      appendTransaction(transactions, _oldState, newState) {
        if (!isSlashSkillsEnabled()) {
          return null;
        }

        if (transactions.some((tr) => tr.getMeta(WORKSPACE_AGENT_SKILL_AUTO_FORMAT_PLUGIN_KEY))) {
          return null;
        }

        const catalogUpgradeRequested = transactions.some((tr) =>
          tr.getMeta(WORKSPACE_AGENT_SKILL_CATALOG_UPGRADE_REQUEST_META),
        );

        if (!catalogUpgradeRequested && !transactions.some((tr) => tr.docChanged)) {
          return null;
        }

        const autoFormatTr = tryAppendWorkspaceAgentSlashCommandAutoFormat(newState, {
          getSkills,
          hasSkillsCatalogNextPage,
          requestSkillsCatalogNextPage,
        });

        if (!autoFormatTr) {
          return null;
        }

        return autoFormatTr.setMeta(WORKSPACE_AGENT_SKILL_AUTO_FORMAT_PLUGIN_KEY, true);
      },
      key: WORKSPACE_AGENT_SKILL_AUTO_FORMAT_PLUGIN_KEY,
    });

    return [
      Suggestion({
        editor: this.editor,
        ...this.options.suggestion,
      }),
      catalogUpgradePlugin,
      autoFormatPlugin,
    ];
  },

  name: 'workspaceAgentSkillSuggestion',
});
